/** WCAG 2.x contrast math + AA-guaranteeing color adjustment. */

import { hexToRgb, rgbToHsl, hslToRgb } from './convert.js';
import type { RGB } from './types.js';

/** WCAG normal-text AA threshold. */
export const AA_NORMAL = 4.5;
/** WCAG large-text / UI-component AA threshold. */
export const AA_LARGE = 3.0;

const BLACK: RGB = { r: 0, g: 0, b: 0 };
const WHITE: RGB = { r: 255, g: 255, b: 255 };

function channelLuminance(c8: number): number {
  const c = c8 / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance of an sRGB color. */
export function relativeLuminance(rgb: RGB): number {
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
}

function toRgb(c: RGB | string): RGB {
  return typeof c === 'string' ? hexToRgb(c) : c;
}

/** WCAG contrast ratio in [1, 21]. Symmetric in its arguments. */
export function contrastRatio(a: RGB | string, b: RGB | string): number {
  const la = relativeLuminance(toRgb(a));
  const lb = relativeLuminance(toRgb(b));
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

export function meetsAA(fg: RGB | string, bg: RGB | string, large = false): boolean {
  return contrastRatio(fg, bg) >= (large ? AA_LARGE : AA_NORMAL);
}

/** Black or white — whichever contrasts the background better. */
export function bestTextOn(bg: RGB | string): RGB {
  const rbg = toRgb(bg);
  return contrastRatio(WHITE, rbg) >= contrastRatio(BLACK, rbg) ? WHITE : BLACK;
}

/**
 * Return `color` nudged in lightness until it meets `target` contrast against
 * `bg`, preserving hue/saturation as far as possible. Moves AWAY from the
 * background's luminance (darker on a light bg, lighter on a dark bg). Falls
 * back to black/white if hue-preserving adjustment can't reach the target.
 */
export function ensureContrast(color: RGB, bg: RGB, target: number = AA_NORMAL): RGB {
  if (contrastRatio(color, bg) >= target) return color;

  const goDarker = relativeLuminance(bg) > 0.5;
  const hsl = rgbToHsl(color);
  const step = 0.02;
  let l = hsl.l;

  for (let i = 0; i < 60; i++) {
    l = goDarker ? l - step : l + step;
    if (l <= 0 || l >= 1) break;
    const candidate = hslToRgb({ ...hsl, l });
    if (contrastRatio(candidate, bg) >= target) return candidate;
  }
  // Hue-preserving adjustment maxed out — use pure black/white.
  const extreme = goDarker ? BLACK : WHITE;
  return contrastRatio(extreme, bg) >= contrastRatio(hslToRgb({ ...hsl, l }), bg)
    ? extreme
    : hslToRgb({ ...hsl, l: goDarker ? 0 : 1 });
}

/**
 * Produce an accessible {fill, fg} button pair from a base color. Nudges the
 * FILL lightness (darker in light mode, lighter in dark mode) until a
 * black-or-white foreground meets AA on it. Guarantees the returned pair
 * satisfies AA. In dark mode this naturally lifts a dark brand so it's both
 * visible on a dark canvas and legible.
 */
export function accessibleFill(base: RGB, mode: 'light' | 'dark'): { fill: RGB; fg: RGB } {
  let fill = base;
  const step = 0.02;
  for (let i = 0; i < 60; i++) {
    const fg = bestTextOn(fill);
    if (contrastRatio(fg, fill) >= AA_NORMAL) return { fill, fg };
    const hsl = rgbToHsl(fill);
    const l = mode === 'light' ? hsl.l - step : hsl.l + step;
    if (l <= 0 || l >= 1) break;
    fill = hslToRgb({ ...hsl, l });
  }
  return { fill, fg: bestTextOn(fill) };
}

/**
 * The house's colour at near-black — a dark panel surface in the tenant's hue.
 *
 * Jim, 2026-09-11, on the web catalogue's sale header and filter rail: *"we need
 * to try darker colors on the info header box and filters. Try the dark,
 * almost-black color from DTA's color scheme."*
 *
 * ⚠️ THAT COLOUR IS NOT STORED ANYWHERE. Discount Tool Auctions holds exactly
 * two: `primary_color` #555555 and `accent_color` #faf1a5. Their public site
 * uses a near-black bar, but no field has ever captured it, and #555555 is a mid
 * grey rather than an almost-black. So this derives one.
 *
 * 🔴 NOT A LITERAL BLACK. That is the easy answer and the wrong one — every
 * white-labelled house would get the same panel, which is the opposite of what a
 * branded surface is for.
 *
 * 🔴 AND NOT A FIXED MIX TOWARD BLACK. A mix lands wherever the house's own
 * lightness happens to start: #555555 would reach near-black while a pale-yellow
 * house stayed a mid olive. Pinning the LIGHTNESS means every house arrives at a
 * comparable darkness wearing its own hue.
 *
 *   #555555 → #1f1f1f   #0a0045 → #140f2e   #faf1a5 → #2e2b0f   #8b1a1a → #2e0f0f
 *
 * Saturation is capped on the way down because a vivid hue at low lightness
 * reads as a colour field rather than as a dark surface, and this is a
 * BACKGROUND that body text sits on.
 */
export function deepSurface(primary: RGB | string): RGB {
  const hsl = rgbToHsl(toRgb(primary));
  return hslToRgb({ h: hsl.h, s: Math.min(hsl.s, DEEP_MAX_SATURATION), l: DEEP_LIGHTNESS });
}

/** Dark enough to read as almost-black on any hue; light enough not to crush. */
const DEEP_LIGHTNESS = 0.12;
const DEEP_MAX_SATURATION = 0.5;

/**
 * A MUTED foreground on an arbitrary surface, floored at AA.
 *
 * 🔴 WHY THIS IS NOT A FIXED MIX. The dark panels started life with their muted
 * text written in CSS as `color-mix(… var(--text-on-deep) 72%, var(--brand-deep))`,
 * which was measured and safe — against a DERIVED panel, whose lightness is
 * pinned at 0.12. The moment a house could store its own colour that guarantee
 * evaporated: Discount Tool Auctions' own darkest brand colour is #56555b, a mid
 * charcoal, and the same 72% step measured **4.61:1** there against 8.40:1 on
 * the near-black. Still passing, but with nothing left — and a house one shade
 * lighter would have failed outright with every token "correct".
 *
 * So the mix is a STARTING POINT and the floor is the guarantee: step back
 * toward the full foreground until the ratio clears. A surface that cannot carry
 * muted text at all simply gets the full foreground, which is legible and merely
 * loses a little hierarchy — the right way to fail.
 */
export function mutedOn(fg: RGB | string, bg: RGB | string, target: number = AA_NORMAL): RGB {
  const f = toRgb(fg);
  const b = toRgb(bg);
  for (let weight = MUTED_START; weight <= 1; weight += 0.04) {
    const candidate: RGB = {
      r: Math.round(b.r + (f.r - b.r) * weight),
      g: Math.round(b.g + (f.g - b.g) * weight),
      b: Math.round(b.b + (f.b - b.b) * weight),
    };
    if (contrastRatio(candidate, b) >= target) return candidate;
  }
  return f;
}

/** The hierarchy we want when the surface can afford it. */
const MUTED_START = 0.72;
